import "../k";
import { addButton } from "./button";

const MAX_DIMENSION = 360;
const FRAME_PADDING = 68; // 34px on each side

// Preload video to get its natural dimensions
function getVideoDimensions(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const tempVideo = document.createElement('video');
    tempVideo.src = url;
    tempVideo.addEventListener('loadedmetadata', () => {
      resolve({ width: tempVideo.videoWidth, height: tempVideo.videoHeight });
      tempVideo.remove();
    });
    // Fallback if metadata doesn't load
    tempVideo.addEventListener('error', () => {
      resolve({ width: MAX_DIMENSION, height: MAX_DIMENSION });
      tempVideo.remove();
    });
  });
}

function calculateDimensions(naturalWidth: number, naturalHeight: number) {
  const aspectRatio = naturalWidth / naturalHeight;
  let videoWidth: number;
  let videoHeight: number;

  if (aspectRatio > 1) {
    // Landscape: keep max width, calculate height
    videoWidth = MAX_DIMENSION;
    videoHeight = MAX_DIMENSION / aspectRatio;
  } else {
    // Portrait or square: keep max height, calculate width
    videoHeight = MAX_DIMENSION;
    videoWidth = MAX_DIMENSION * aspectRatio;
  }

  return {
    videoWidth,
    videoHeight,
    frameWidth: videoWidth + FRAME_PADDING,
    frameHeight: videoHeight + FRAME_PADDING,
  };
}

async function addVideoPlayer(url: string, isReal: boolean, comps?: any[]) {
  // Get natural dimensions first
  const natural = await getVideoDimensions(url);
  const dims = calculateDimensions(natural.width, natural.height);

  const frame = add([
    sprite("frame", { width: dims.frameWidth, height: dims.frameHeight }),
    anchor("center"),
    animate(),
    area(),
    { isEnabled: true, isPlaying: false, isInspectable: true },
    ...comps,
  ]);

  const vid = frame.add([
    video(url, { width: dims.videoWidth, height: dims.videoHeight }),
    anchor("center"),
    z(1),
    pos(),
  ]);

  // Mute by default
  vid.mute = true;

  // Play button OUTSIDE the frame (below it)
  const BUTTON_Y = dims.frameHeight / 2 + 60;
  const playButton = addButton("button", [z(10)], vec2(102, 102));

  const ICON_COLOR = BLACK;

  const iconOffset = playButton.add([
    pos(0, -4),
  ]);

  const playIcon = iconOffset.add([
    polygon([vec2(-10, -14), vec2(-10, 14), vec2(14, 0)]),
    anchor("center"),
    color(ICON_COLOR),
    z(11),
  ]);

  const pauseIcon1 = iconOffset.add([
    rect(8, 28),
    anchor("center"),
    color(ICON_COLOR),
    z(11),
    pos(-8, 0),
  ]);
  pauseIcon1.hidden = true;

  const pauseIcon2 = iconOffset.add([
    rect(8, 28),
    anchor("center"),
    color(ICON_COLOR),
    z(11),
    pos(8, 0),
  ]);
  pauseIcon2.hidden = true;

  // Cleanup button when frame is destroyed
  frame.onDestroy(() => {
    playButton.destroy();
  });

  frame.animate("scale", [vec2(0.8, 0.8), vec2(1, 1)], {
    duration: 0.5,
    easing: easings.easeOutElastic,
    loops: 1,
  });

  let isAnticipating = false, isHovering = false;
  let hoverStartTime = 0;

  const updateIcons = () => {
    playIcon.hidden = frame.isPlaying;
    pauseIcon1.hidden = !frame.isPlaying;
    pauseIcon2.hidden = !frame.isPlaying;
  };

  const togglePlayback = () => {
    if (frame.isPlaying) {
      vid.pause();
      frame.isPlaying = false;
    } else {
      vid.play();
      frame.isPlaying = true;
    }
    updateIcons();
  };

  // Click on play button toggles playback (doesn't trigger selection)
  playButton.onClick(() => {
    if (frame.isEnabled) {
      togglePlayback();
    }
  });

  // Start playing automatically after a short delay
  wait(0.1, () => {
    vid.play();
    frame.isPlaying = true;
    updateIcons();
  });

  frame.onHoverUpdate(() => {
    if (!isHovering) {
      hoverStartTime = time();
      isHovering = true;
    }

    if (!frame.isEnabled) {
      frame.scale = vec2(1, 1);
      frame.color = Color.WHITE;
      setCursor("default");
      isAnticipating = false;
      return;
    }
    const t = time() * 10;
    const pulse = Math.sin(t) * 10 + 230;
    frame.scale = vec2(1.05);
    frame.color = rgb(pulse, pulse, pulse);
    setCursor("pointer");
    if (!isAnticipating) {
      play("rollover", { volume: 0.2 });
      isAnticipating = true;
    }
  });

  frame.onHoverEnd(() => {
    frame.scale = vec2(1, 1);
    frame.color = Color.WHITE;
    setCursor("default");
    isHovering = false;
    isAnticipating = false;
    hoverStartTime = 0;
  });

  frame.onClick(() => {
    if (frame.isEnabled) {
      play("click", { volume: 0.5 });
    }
  });

  // Sync button state and position with frame
  frame.onUpdate(() => {
    playButton.isEnabled = frame.isEnabled;
    playButton.pos = frame.pos.add(vec2(0, BUTTON_Y));
  });

  // Zoom/pan interaction
  let targetVidWidth = dims.videoWidth;
  let targetVidHeight = dims.videoHeight;
  let targetVidPos = vec2(0);
  const LERP_SPEED = 16;

  // Spring simulation variables for position
  let currentVidPos = vec2(0);
  let velocity = vec2(0);
  const SPRING_STIFFNESS = 250;
  const SPRING_DAMPING = 15;

  let isZoomed: boolean;

  vid.onUpdate(() => {
    const HOVER_DELAY = 0.5;
    const shouldZoom = frame.isInspectable && isHovering && (time() - hoverStartTime >= HOVER_DELAY);

    // Play zoom sounds
    if (shouldZoom && !isZoomed) {
      play("zoomIn", { volume: 0.3 });
      isZoomed = true;
    }
    if (!shouldZoom && isZoomed) {
      play("zoomOut", { volume: 0.2 });
      isZoomed = false;
    }

    const factor = 1.75;
    targetVidWidth = shouldZoom ? dims.videoWidth * factor : dims.videoWidth;
    targetVidHeight = shouldZoom ? dims.videoHeight * factor : dims.videoHeight;

    // Calculate target position for spring
    let newTargetVidPos = vec2(0);
    vid.z = 5;

    if (shouldZoom) {
      vid.z = 15;
      const delta = frame.pos.sub(mousePos()).scale(Math.log2(factor));
      newTargetVidPos = delta;
    }
    targetVidPos = newTargetVidPos;

    // Apply lerp for smooth width and height transitions
    vid.width = lerp(vid.width, targetVidWidth, dt() * LERP_SPEED);
    vid.height = lerp(vid.height, targetVidHeight, dt() * LERP_SPEED);

    // Apply spring simulation for smooth position transition
    const displacement = targetVidPos.sub(currentVidPos);
    const acceleration = displacement.scale(SPRING_STIFFNESS).sub(velocity.scale(SPRING_DAMPING));
    velocity = velocity.add(acceleration.scale(dt()));
    currentVidPos = currentVidPos.add(velocity.scale(dt()));
    vid.pos = currentVidPos;
  });

  // Method to stop video when question is answered
  frame.stop = () => {
    vid.pause();
    frame.isPlaying = false;
  };

  return frame;
}

export { addVideoPlayer };
